import { useMemo } from "react";
import type { ResultColumn, ResultEnvelope, Row, Visualization } from "@cd/core";
import { buildPivot, canPick, formatTimeBucket, formatValue, gradientDomain, gradientFill, humanize, isDimmed, isGradientScheme, type Highlight } from "@cd/viz";
import { useResolvedTheme } from "../../lib/theme";
import { cn } from "../../lib/utils";
import type { OnPick } from "./onPick";

const fmtDim = (c: ResultColumn, v: Row[string] | undefined) => (c.type === "time" ? formatTimeBucket(v, c.grain) : humanize(v));

export function Pivot({ viz, result, onPick, highlight }: { viz: Visualization; result: ResultEnvelope; onPick?: OnPick; highlight?: Highlight[] }) {
  // Row and column headers drill down; cells hold totals of two groupings at once, so they don't.
  const pickable = (c: ResultColumn) => !!onPick && canPick(c);
  const pick = (c: ResultColumn, v: Row[string] | undefined) => (e: { clientX: number; clientY: number }) =>
    onPick?.([{ column: c, value: v ?? null }], { x: e.clientX, y: e.clientY });
  const mode = useResolvedTheme();
  const scheme = isGradientScheme(viz.config.colorScale) ? viz.config.colorScale : "blue";
  const reverse = viz.config.colorReverse === true;
  const { rowDims, colDim, metric, rowKeys, colKeys, cells, domain } = useMemo(() => {
    const p = buildPivot(result);
    return { ...p, domain: gradientDomain(p.cells.values()) };
  }, [result]);
  const colDimmed = (v: Row[string] | undefined) => isDimmed(highlight, colDim.key, v ?? null);

  return (
    <div className="size-full overflow-auto">
      <table className="border-separate border-spacing-0 text-sm">
        <thead className="sticky top-0 z-10 bg-surface">
          <tr>
            {rowDims.map((d) => (
              <th key={d.key} className="sticky left-0 border-b border-border bg-surface px-3 py-2 text-left text-xs font-medium text-muted">{d.label}</th>
            ))}
            {colKeys.map(([k, v]) => (
              <th
                key={k}
                onClick={pickable(colDim) ? pick(colDim, v) : undefined}
                className={cn(
                  "border-b border-border px-3 py-2 text-right text-xs font-medium whitespace-nowrap text-muted",
                  pickable(colDim) && "cursor-pointer hover:text-foreground hover:underline", colDimmed(v) && "opacity-40",
                )}
              >
                {fmtDim(colDim, v)}
              </th>
            ))}
            {metric.additive && <th className="border-b border-border px-3 py-2 text-right text-xs font-semibold">Total</th>}
          </tr>
        </thead>
        <tbody>
          {rowKeys.map(([rk, r]) => {
            let total = 0;
            // Only the row groupings: a row header's sample row also carries some column value.
            const rowDimmed = rowDims.some((d) => isDimmed(highlight, d.key, r[d.key] ?? null));
            return (
              <tr key={rk} className={cn(rowDimmed && "opacity-40")}>
                {rowDims.map((d) => (
                  <td
                    key={d.key}
                    onClick={pickable(d) ? pick(d, r[d.key]) : undefined}
                    className={cn("sticky left-0 border-b border-border bg-surface px-3 py-1.5 whitespace-nowrap", pickable(d) && "cursor-pointer hover:underline")}
                  >
                    {fmtDim(d, r[d.key])}
                  </td>
                ))}
                {colKeys.map(([ck, cv]) => {
                  const v = cells.get(`${rk}|${ck}`) ?? null;
                  if (typeof v === "number") total += v;
                  // Color intensity encodes magnitude; the number is always printed.
                  return (
                    <td
                      key={ck}
                      className={cn("tabular border-b border-border px-3 py-1.5 text-right whitespace-nowrap", !rowDimmed && colDimmed(cv) && "opacity-40")}
                      style={{ background: gradientFill(scheme, v, domain, mode, reverse) }}
                    >
                      {formatValue(v, metric.format, { compact: true })}
                    </td>
                  );
                })}
                {metric.additive && (
                  <td className="tabular border-b border-border px-3 py-1.5 text-right font-medium">{formatValue(total, metric.format, { compact: true })}</td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
